"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { useEffect, useMemo, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  extractProperty,
  FacilityContent,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
} from "utils"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

import { Button, Link } from "@mui/material"
import "swiper/css"
import "swiper/css/effect-fade"
import { Location3Icon, PriceIcon, UserIcon } from "./icons"

const TRANSITION = `all 0.36s cubic-bezier(0.34, 1.34, 1, 1)`
const PORT_BLUE_IMAGE_URL = "/images/port-blue-static.png"

const StyledSection = styled.section`
  padding-top: 60px;
  padding-bottom: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 24px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
    padding: 90px 0 20px;
  }
  &::after {
    content: url(${PORT_BLUE_IMAGE_URL});
    display: none;

    position: absolute;
    z-index: 1;

    ${(props) => props.theme.breakpoints.up("md")} {
      /* display: flex; */

      transform: rotate(-179.689deg);
      inset-inline-end: unset;
      inset-inline-start: -5.924px;
      bottom: -166.604px;
    }
  }
`

const StyledWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 48px;
  }
`
const StyledFacilityTabs = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px 60px;
  }
`

const StyledFacilityTabItem = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.propBlue : theme.colors.port.defaultTextColor};

  font-size: 20px;
  font-weight: 700;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    text-transform: capitalize;
  }
`

const StyledSlider = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 26px;
  }
`

const StyledImageSwiper = styled.div`
  width: 100%;
  max-width: 1188px;
  margin: 0 auto;
  .swiper,
  .swiper-wrapper {
    overflow: visible;
  }
  .swiper {
    padding-block: 0;
  }
  .swiper-wrapper {
    transition-timing-function: ease-in-out;
  }
  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 300px;
    height: 275px;
    .image-wrapper {
      padding: 23px 24px;
    }
    &.swiper-slide-active {
      .image-wrapper {
        padding: 0;
        &::after {
          opacity: 1;
        }
        .hover-content {
          opacity: 1;
          bottom: 32px;
        }
      }
    }
    ${(props) => props.theme.breakpoints.up("md")} {
      width: 490px;
      height: 450px;
      .image-wrapper {
        padding: 46px 60px;
      }
      &.swiper-slide-active {
        width: 490px;
        height: 450px;

        .image-wrapper {
          &::after {
            opacity: 0;
          }
          .hover-content {
            opacity: 0;
          }

          &:not(.animating):hover {
            &::after {
              opacity: 1;
            }
            .hover-content {
              opacity: 1;
              bottom: 32px;
            }
          }
        }
      }
    }
  }
`

const StyledSwiperImageWrapper = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  overflow: hidden;
  transition: ${TRANSITION};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: "";
    background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: ${TRANSITION};
    transition-duration: 700ms;
  }
`

const StyledSwiperImageWrapperHover = styled.div`
  z-index: 10;
  position: absolute;
  width: max-content;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  opacity: 0;
  color: white;
  transition: ${TRANSITION};
  transition-duration: 700ms;
  ${(props) => props.theme.breakpoints.up("md")} {
    bottom: -50%;
  }
`

const StyledDesktopLinkButton = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  display: flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 45px;
  width: max-content;
  height: max-content;
  color: white;
  border: 1px solid white;
  background-color: rgba(14, 40, 70, 0.2);
  transition: background-color 0.3s ease;

  font-size: 14px;
  font-weight: 700;
  &:hover {
    background-color: rgba(14, 40, 70, 0.3);
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const FacilityHoverText = styled.h5`
  margin: 0;
  padding: 0;

  font-size: 30px;
  font-weight: 400;
  line-height: 125%; /* 37.5px */
  letter-spacing: -0.6px;
  text-transform: capitalize;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
  }
`

const StyledDetailSwiper = styled.div`
  width: 100%;
  max-width: 490px;
  align-self: center;

  .swiper {
    padding-block: 0;
    padding-bottom: 40px;
  }

  .swiper-slide {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    text-align: center;
    gap: 16px;
  }

  .swiper-slide {
    opacity: 0 !important;
  }
  .swiper-slide-active {
    opacity: 1 !important;
  }
`

const StyledDetailTop = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`

const StyledDetailTopItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  address,
  span {
    font-style: normal;
    text-decoration: none;
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 18px;
    }
  }
  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }
`

const StyledDetailDescription = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  color: black;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    color: inherit;
  }
`

type PropsPortFacilitiesHP = {
  data: any
  ports?: PortItem[]
}

const PortFacilitiesHP = ({ data, ports }: PropsPortFacilitiesHP) => {
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")

  const filteredFacilities: FacilityContent[] = useMemo(() => {
    const allFacilities = ports?.flatMap((port) => port.portFacilities || []) || []
    const itemsList = extractProperty(data, "facilities") || []
    const filtered: FacilityContent[] =
      itemsList?.map((item: any) => allFacilities?.find((facility) => item.url === facility.url)) ||
      []
    return filtered
  }, [data, ports])

  // Extract unique facility types for tabs
  const facilityTypes = useMemo(() => {
    return Array.from(new Set(filteredFacilities.map((f) => f.facilityType).filter(Boolean)))
  }, [filteredFacilities])

  // Active filter state - defaults to first facility type
  const [activeFilter, setActiveFilter] = useState<string | null>(null)

  // Initialize activeFilter to second facility type if available, otherwise first
  useEffect(() => {
    if (facilityTypes.length > 0 && activeFilter === null) {
      setActiveFilter(facilityTypes.length > 1 ? facilityTypes[1] : facilityTypes[0])
    }
  }, [facilityTypes, activeFilter])

  // Filter facilities based on active filter
  const displayFacilities = useMemo(() => {
    if (!activeFilter) return filteredFacilities
    return filteredFacilities.filter((f) => f.facilityType === activeFilter)
  }, [filteredFacilities, activeFilter])

  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const swiperRef = useRef<SwiperType | null>(null)
  const detailSwiperRef = useRef<SwiperType | null>(null)
  const [swiperReady, setSwiperReady] = useState(false)

  const handleSlideClick = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index)
    }
  }

  const handleTransitionStart = () => {
    setIsAnimating(true)
  }

  const handleTransitionEnd = () => {
    setIsAnimating(false)
  }

  // Reset swiper ready state when filter changes
  useEffect(() => {
    setSwiperReady(false)
  }, [activeFilter])

  // Set initial slide after swiper is ready
  useEffect(() => {
    if (swiperReady && displayFacilities.length > 1) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (swiperRef.current && swiperRef.current.activeIndex !== 1) {
          swiperRef.current.slideTo(1, 0)
        }
        if (detailSwiperRef.current && detailSwiperRef.current.activeIndex !== 1) {
          detailSwiperRef.current.slideTo(1, 0)
        }
      })
    }
  }, [swiperReady, displayFacilities.length])

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && filteredFacilities.length === 0) {
    return null
  }

  // Hide section if no facilities match the current filter
  if (displayFacilities.length === 0) {
    return null
  }

  return (
    <StyledSection>
      <PortContainer paddingInline={20}>
        <PortTitle normalText={blackTitle} centered highlightedText={blueTitle} />
      </PortContainer>
      <PortContainer paddingInline={20} maxWidth={1280}>
        <StyledWrapper>
          <StyledFacilityTabs>
            {facilityTypes.map((type) => (
              <StyledFacilityTabItem
                key={type}
                aria-label="facility tab"
                type="button"
                $active={activeFilter === type}
                onClick={() => {
                  setActiveFilter(type)
                }}
              >
                {type}
              </StyledFacilityTabItem>
            ))}
          </StyledFacilityTabs>
          <StyledSlider>
            <StyledImageSwiper>
              <Swiper
                key={activeFilter}
                initialSlide={displayFacilities.length > 1 ? 1 : 0}
                slidesPerView={"auto"}
                centeredSlides
                speed={360}
                allowTouchMove={true}
                simulateTouch={true}
                breakpoints={{
                  769: {
                    allowTouchMove: false,
                    simulateTouch: false,
                  },
                }}
                onSlideChange={(swiper) => {
                  if (
                    detailSwiperRef.current &&
                    detailSwiperRef.current.activeIndex !== swiper.activeIndex
                  ) {
                    detailSwiperRef.current.slideTo(swiper.activeIndex)
                  }
                }}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper
                }}
                onInit={() => {
                  setSwiperReady(true)
                }}
                onTransitionStart={handleTransitionStart}
                onTransitionEnd={handleTransitionEnd}
              >
                {displayFacilities.map(({ image, hoverButton, hoverText }, index) => (
                  <SwiperSlide key={`image-slide-${index}`} onClick={() => handleSlideClick(index)}>
                    <StyledSwiperImageWrapper
                      className={`image-wrapper ${isAnimating ? "animating" : ""}`}
                    >
                      {image && (
                        <Image
                          src={getUmbracoMediaUrl(image)}
                          width={490}
                          height={450}
                          alt={"Facility image"}
                        />
                      )}
                      <StyledSwiperImageWrapperHover className="hover-content">
                        {hoverText && <FacilityHoverText>{hoverText}</FacilityHoverText>}
                        {hoverButton?.name && hoverButton?.url && (
                          <StyledDesktopLinkButton
                            href={getNonUmbracoUrl(hoverButton.url)}
                            target={hoverButton.target || "_self"}
                          >
                            {hoverButton.name}
                          </StyledDesktopLinkButton>
                        )}
                      </StyledSwiperImageWrapperHover>
                    </StyledSwiperImageWrapper>
                  </SwiperSlide>
                ))}
              </Swiper>
            </StyledImageSwiper>
            <StyledDetailSwiper>
              <Swiper
                key={activeFilter}
                initialSlide={displayFacilities.length > 1 ? 1 : 0}
                slidesPerView={1}
                speed={360}
                allowTouchMove={false}
                simulateTouch={false}
                effect="fade"
                modules={[EffectFade]}
                onSwiper={(swiper) => {
                  detailSwiperRef.current = swiper
                }}
              >
                {displayFacilities.map(
                  ({ address, amountOfPeople, button, description, price }, index) => {
                    return (
                      <SwiperSlide key={`detail-slide-${index}`}>
                        <StyledDetailTop>
                          {amountOfPeople && (
                            <StyledDetailTopItem>
                              <UserIcon />
                              <span>{amountOfPeople}</span>
                            </StyledDetailTopItem>
                          )}
                          {address && (
                            <StyledDetailTopItem>
                              <Location3Icon />
                              <address>{address}</address>
                            </StyledDetailTopItem>
                          )}
                          {price && (
                            <StyledDetailTopItem>
                              <PriceIcon />
                              <span>{price}</span>
                            </StyledDetailTopItem>
                          )}
                        </StyledDetailTop>
                        {description && (
                          <StyledDetailDescription>{description}</StyledDetailDescription>
                        )}
                        {button?.url && button?.name && (
                          <Button
                            variant="contained"
                            sx={{
                              fontSize: {
                                xs: "18px",
                              },
                              minWidth: {
                                xs: "103px",
                                md: "200px",
                              },
                              justifySelf: "center",
                              height: {
                                xs: "48px",
                              },
                            }}
                            aria-label="facility detail button"
                            target={"_self"}
                            href={getNonUmbracoUrl(button.url)}
                            onClick={(e) => {
                              const href = getNonUmbracoUrl(button.url)
                              if (href.includes("#")) {
                                e.preventDefault()
                                const anchor = href.split("#")[1]
                                const element = document.getElementById(anchor)
                                if (element) {
                                  element.scrollIntoView({ behavior: "smooth" })
                                }
                              }
                            }}
                          >
                            {button.name}
                          </Button>
                        )}
                      </SwiperSlide>
                    )
                  },
                )}
              </Swiper>
            </StyledDetailSwiper>
          </StyledSlider>
        </StyledWrapper>
      </PortContainer>
    </StyledSection>
  )
}
export default PortFacilitiesHP
